import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { CYF_DEAL_TYPE } from '../../brands/default/config';
import { BASKET_DEAL_PRODUCT_META } from '../../brands/default/content/components/checkout';
import { VIP_LAYOUT_TYPE } from '../../config/constants/vip';
import { formatCurrency } from '../../helpers/currency';
import { isMobileViewport } from '../../helpers/screen';
import ThemeContext from '../../providers/ThemeProvider';
import BasketDealProductDelete from './BasketDealProductDelete';
import BasketDealProductMetaitem from './BasketDealProductMetaItem';
import BasketDealProductQuantity from './BasketDealProductQuantity';

export default function BasketDealProductMeta({
  bt,
  giftWrap,
  isAuthenticated,
  isChannelManagerDeal,
  deal,
  product,
  remainingQuantity,
  welcomeVipEnabled = false,
}) {
  const theme = useContext(ThemeContext);
  const flightDeal = deal.dealType === CYF_DEAL_TYPE;
  const checkoutData = useSelector((state) => state.checkout.checkoutData);
  const isModernVip =
    checkoutData?.vipSubscriptionDetails?.userInterfaceVariant ===
    VIP_LAYOUT_TYPE.MODERN;
  const isMobile = isMobileViewport();

  return (
    <>
      <div className="meta">
        {/* SAVE */}
        <BasketDealProductMetaitem
          className="meta-item--save"
          hidden={!product.displayDiscount}
          label={
            isChannelManagerDeal
              ? BASKET_DEAL_PRODUCT_META.saveUpto
              : BASKET_DEAL_PRODUCT_META.save
          }
        >
          {product.discountPercentage}%
        </BasketDealProductMetaitem>

        {/* NOW */}
        {!isModernVip && (
          <BasketDealProductMetaitem
            className="meta-item--now"
            hidden={Boolean(product.depositProduct) && product.payDeposit}
            label={BASKET_DEAL_PRODUCT_META.now}
            primary
          >
            {formatCurrency(deal?.currency, product.price, true, true)}
          </BasketDealProductMetaitem>
        )}

        {/* WAS - replaces NOW for modern VIP */}
        {isModernVip && (
          <BasketDealProductMetaitem
            className="meta-item--now"
            hidden={
              !product.displayDiscount ||
              product.originalPrice === product.price ||
              product.originalPrice === 0
            }
            label={BASKET_DEAL_PRODUCT_META.was}
            primary
          >
            {formatCurrency(deal?.currency, product.originalPrice, true, true)}
          </BasketDealProductMetaitem>
        )}

        {/* DEPOSIT */}
        <BasketDealProductMetaitem
          className="meta-item--deposit"
          hidden={!product.payDeposit}
          label={BASKET_DEAL_PRODUCT_META.deposit}
          primary
        >
          {formatCurrency(
            deal?.currency,
            product.depositProduct
              ? product.depositProduct.price
              : product.price,
            true,
            true,
          )}
        </BasketDealProductMetaitem>

        {/* QUANTITY */}
        {!isChannelManagerDeal && (
          <BasketDealProductMetaitem
            className="meta-item--quantity"
            label={BASKET_DEAL_PRODUCT_META.quantity}
          >
            <BasketDealProductQuantity
              bt={bt}
              deal={deal}
              flightDeal={flightDeal}
              giftWrap={giftWrap}
              isAuthenticated={isAuthenticated}
              product={product}
              remainingQuantity={remainingQuantity}
            />
          </BasketDealProductMetaitem>
        )}

        {isModernVip &&
          product.totalProductVipAmount > 0 &&
          welcomeVipEnabled && (
            <BasketDealProductMetaitem className="meta-item--vip" isModernVip>
              {formatCurrency(
                deal.currency,
                product.totalProductVipAmount,
                true,
                false,
                false,
              )}
              <div className="meta-item--vip__message">
                Save an extra{' '}
                {formatCurrency(
                  deal.currency,
                  product.vipDiscountAmount,
                  true,
                  false,
                  false,
                )}{' '}
                off!
              </div>
            </BasketDealProductMetaitem>
          )}

        {/* SUBTOTAL */}
        <BasketDealProductMetaitem
          className="meta-item--subtotal"
          label={BASKET_DEAL_PRODUCT_META.subtotal}
        >
          {formatCurrency(
            deal?.currency,
            product.totalProductAmount,
            true,
            false,
            false,
          )}
        </BasketDealProductMetaitem>
        {/* Delete for CM deal */}
        {isChannelManagerDeal && (
          <BasketDealProductDelete
            bt={bt}
            giftWrap={giftWrap}
            product={product}
          />
        )}
      </div>
      <style jsx>{`
        .meta {
          display: flex;
          justify-content: flex-end;
          width: 100%;
        }
        @media (max-width: ${theme.breakpoints.lgDown}) {
          :global(.meta .meta-item--now),
          :global(.meta .meta-item--deposit) {
            display: none;
          }
        }
        .meta-item--vip__message {
          font-size: ${isMobile ? '6px' : '9px'};
        }
      `}</style>
    </>
  );
}

BasketDealProductMeta.propTypes = {
  bt: PropTypes.string,
  deal: PropTypes.object,
  giftWrap: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isChannelManagerDeal: PropTypes.bool,
  product: PropTypes.object,
  remainingQuantity: PropTypes.number,
};
